<template>
    <div class="checkbox-row" :class="{ 'is-emphasized': emphasized }" :style="colorStyle">
        <div class="is-flex is-justify-content-space-between is-align-items-center">
            <Checkbox2 :model-value="modelValue" :disabled="disabled" @update:model-value="emit('update:modelValue', $event)">
                <slot></slot>
            </Checkbox2>
            <span v-if="tagLabel" class="tag" :class="`is-${tagColor ?? color}`">
                {{ tagLabel }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import Checkbox2 from "./Checkbox2.vue";

    type BulmaColor = "primary" | "info" | "success" | "warning" | "danger" | "link";

    const props = withDefaults(
        defineProps<{
            modelValue: boolean;
            disabled?: boolean;
            /** Bulma color driving the emphasized border/background and the default tag color. */
            color?: BulmaColor;
            /** Highlights the row border and tints the background using `color`. */
            emphasized?: boolean;
            /** Text for the optional tag floated to the right; the tag is omitted when unset. */
            tagLabel?: string;
            /** Overrides the tag color; defaults to `color`. */
            tagColor?: BulmaColor;
        }>(),
        {
            color: "primary",
        },
    );

    const emit = defineEmits<{
        "update:modelValue": [boolean];
    }>();

    const colorStyle = computed(() => ({ "--checkbox-row-color": `var(--bulma-${props.color})` }));
</script>

<style scoped>
    .checkbox-row {
        border: 1px solid var(--bulma-border);
        border-radius: var(--bulma-radius-large);
        padding: 0.75rem 1rem;
        transition:
            border-color 0.15s ease,
            background-color 0.15s ease;
    }

    .checkbox-row.is-emphasized {
        border-color: var(--checkbox-row-color, var(--bulma-primary));
        background-color: color-mix(in srgb, var(--checkbox-row-color, var(--bulma-primary)) 15%, transparent);
    }
</style>
